import { useEffect, useRef, useState } from 'react';

import { filestackPath, ia } from '/lib/helpers/utility';
import { uniqueId } from 'filestack-js';

import generatePdfForm from 'lib/helpers/generatePdfForm/generatePdfForm';

import useFaxActions from 'components/practice/comms_center/fax/lib/useFaxActions';
import useDocumentActions from 'components/shared/DocumentEditor/lib/useDocumentActions';

export default function useSelectFiles({
  handleInsertFiles,
  docsModalVisible,
  documents,
  selectedFiles,
  setLoading = () => {}
}) {
  const inputFieldRef = useRef(null);

  const { renderFax } = useFaxActions({});
  const { renderDocument } = useDocumentActions({});

  const [documentFiles, setDocumentFiles] = useState(documents);

  function createFileFromBuffer(buffer, fileName, mimeType) {
    const blob = new Blob([buffer], { type: mimeType });

    const file = new File([blob], fileName, { type: mimeType });
    file['id'] = file.size;
    file['draggableId'] = uniqueId(12);
    return file;
  }

  useEffect(() => {
    if (docsModalVisible) {
      inputFieldRef.current?.focus();
    }
  }, [docsModalVisible]);

  const selectFiles = async () => {
    const files = ia(documentFiles) ? documentFiles : selectedFiles || [];

    convert(files);
  };

  const convert = async () => {
    try {
      setLoading(true);

      const files = ia(documentFiles) ? documentFiles : selectedFiles || [];

      const arraybufferFiles = [];

      const pdfFiles = await Promise.all(
        files.map(async (file) => {
          const fileType = file.mimetype || file.type;
          const type = file?.form_json
            ? 'pdf'
            : file.kind === 'fax'
              ? 'fax'
              : fileType === 'application/pdf' || fileType === 'pdf'
                ? 'pdf'
                : 'image';

          const blob = file.form_json
            ? await generatePdfForm(file)
            : type === 'fax'
              ? await renderFax(file, true)
              : await renderDocument({
                  file: filestackPath(file.document_handle),
                  type,
                  returnBlob: true,
                  data: file
                });

          if (blob) {
            const arrayBuffer = await blob.arrayBuffer();
            const buffer = new Uint8Array(arrayBuffer);

            const modifiedFile = {
              buffer,
              name: type === 'image' ? file.name.replace(/\.[^/.]+$/, '.png') : file.name,
              originalname: type === 'image' ? file.name.replace(/\.[^/.]+$/, '.png') : file.name,
              mimetype: type === 'image' ? 'image/png' : 'application/pdf',
              size: buffer.byteLength,
              fieldname: 'files',
              id: file.id,
              patientName:
                file.patientName || file.patient
                  ? `${file.patient.f_name} ${file?.patient?.l_name}`
                  : ''
            };

            const fileBuffer = new Uint8Array(modifiedFile.buffer).buffer;
            const createdFile = createFileFromBuffer(
              fileBuffer,
              modifiedFile.name,
              modifiedFile.mimetype
            );

            return createdFile;
          }

          if (type === 'fax') return null;

          return {
            ...file,
            patientName:
              file.patientName || file.patient
                ? `${file.patient.f_name} ${file?.patient?.l_name}`
                : '',
            existingFile: true,
            draggableId: uniqueId(12)
          };
        })
      );

      handleInsertFiles([...arraybufferFiles, ...pdfFiles.filter(Boolean)]);
      setDocumentFiles([]);
    } catch (error) {
      handleInsertFiles();
      console.error('Error converting files:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const select = async () => {
      if (ia(documentFiles)) {
        setTimeout(async () => {
          await selectFiles();
        }, 500);
      }
    };
    select();
  }, [documentFiles]);

  const emptyFiles = () => {
    setDocumentFiles([]);
  };
  return {
    selectFiles,
    emptyFiles
  };
}
